import API_BASE_URL from '../config/api';

const DEFAULT_TIMEOUT_MS = 15000;
const SYSTEM_DATA_TIMEOUT_MS = 600000;

function formatErrorDetail(detail, fallback) {
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return (
      detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (!item?.msg) return JSON.stringify(item);
          const loc = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
          return loc ? `${loc}: ${item.msg}` : item.msg;
        })
        .filter(Boolean)
        .join('; ') || fallback
    );
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
}

function looksLikeHtml(text = '') {
  const sample = String(text || '').trim().slice(0, 64).toLowerCase();
  return sample.startsWith('<!doctype') || sample.startsWith('<html');
}

function requestPath(url = '') {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url || '');
  }
}

function formatSeconds(ms) {
  return `${(ms / 1000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} s`;
}

/** Describe una respuesta HTML inesperada con los datos reales disponibles en el navegador. */
function describeHtmlResponse(response, raw, requestUrl, elapsedMs) {
  const status = response.status;
  const statusLabel = `HTTP ${status}${response.statusText ? ` ${response.statusText}` : ''}`;
  const cache = String(response.headers.get('x-cache') || '');
  const cfId = String(response.headers.get('x-amz-cf-id') || '');
  const title = (String(raw).match(/<title[^>]*>([^<]*)<\/title>/i)?.[1] || '').trim();
  const isAppShell = /id=["']root["']/.test(raw);
  const fromCloudFront = /cloudfront/i.test(cache) || Boolean(cfId);
  const cloudFrontError = /error from cloudfront/i.test(cache);

  const facts = [statusLabel];
  if (Number.isFinite(elapsedMs)) facts.push(`${formatSeconds(elapsedMs)}`);
  if (cache) facts.push(`x-cache: ${cache}`);
  if (title && !isAppShell) facts.push(`página: «${title}»`);

  let cause;
  if (status === 504 || (fromCloudFront && elapsedMs >= 29000)) {
    const cutAt = Number.isFinite(elapsedMs) ? ` (se cortó a los ${formatSeconds(elapsedMs)})` : '';
    cause = `el backend no respondió dentro del límite de espera de CloudFront/proxy${cutAt}.`;
  } else if (status === 502 || status === 503) {
    cause = 'el backend no respondió (caído, reiniciando o sin memoria).';
  } else if (status === 413) {
    cause = 'la solicitud es demasiado grande para el proxy.';
  } else if (isAppShell) {
    cause =
      'CloudFront devolvió la página de la app (index.html) en vez del JSON; suele ocurrir cuando el ' +
      'backend responde 403/404/500 o no responde.';
  } else if (cloudFrontError) {
    cause = 'CloudFront no pudo obtener la respuesta del backend.';
  } else {
    cause = 'el servidor respondió una página HTML en vez de datos.';
  }

  const traceId = cfId ? ` ID CloudFront: ${cfId}.` : '';
  return `El servidor devolvió HTML en lugar de JSON en ${requestPath(requestUrl)} (${facts.join(' · ')}). Probable causa: ${cause}${traceId}`;
}

async function readResponsePayload(response, requestUrl = '', elapsedMs = NaN) {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  const raw = await response.text();

  if (!raw) {
    return { data: null, raw: '' };
  }

  const isJsonContent = contentType.includes('application/json') || contentType.includes('+json');
  if (isJsonContent || (!contentType.includes('text/html') && raw.trim().startsWith('{')) || raw.trim().startsWith('[')) {
    try {
      return { data: JSON.parse(raw), raw };
    } catch {
      /* fall through */
    }
  }

  if (looksLikeHtml(raw) || contentType.includes('text/html')) {
    const htmlError = new Error(describeHtmlResponse(response, raw, requestUrl, elapsedMs));
    htmlError.code = 'HTML_INSTEAD_OF_JSON';
    htmlError.status = response.status;
    throw htmlError;
  }

  try {
    return { data: JSON.parse(raw), raw };
  } catch {
    const parseError = new Error(
      `Respuesta no válida del servidor en ${requestPath(requestUrl)} (HTTP ${response.status}, ` +
        `${contentType || 'sin content-type'}): ${raw.trim().slice(0, 160)}`
    );
    parseError.code = 'INVALID_RESPONSE';
    parseError.status = response.status;
    throw parseError;
  }
}

async function executeAuthenticatedRequest(url, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timeoutMs =
    options.timeoutMs ??
    (String(url || '').includes('/api/system/') ? SYSTEM_DATA_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);
  const { timeoutMs: _ignored, signal: outerSignal, cache: cacheMode, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  if (outerSignal) {
    if (outerSignal.aborted) controller.abort();
    else outerSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    return await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
      cache: cacheMode || 'no-store',
    });
  } catch (error) {
    if (error?.name === 'AbortError' && !outerSignal?.aborted) {
      const timeoutError = new Error(
        `Sin respuesta de ${requestPath(url)} después de ${formatSeconds(timeoutMs)}: ` +
          'se agotó el tiempo de espera del navegador.'
      );
      timeoutError.code = 'TIMEOUT';
      throw timeoutError;
    }
    if (error instanceof TypeError) {
      const networkError = new Error(
        `El navegador no obtuvo respuesta de ${requestPath(url)} (${error.message}). Posibles causas: ` +
          'backend detenido o inaccesible, conexión cortada, o el navegador bloqueó la petición ' +
          '(CORS; el detalle aparece en la consola del navegador).'
      );
      networkError.code = 'NETWORK';
      throw networkError;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

let unauthorizedReloadSuspended = false;

/**
 * Una importación en modo reemplazo vacía users: mientras dura, los 401 de otras llamadas
 * (presencia, avisos) no deben borrar el token ni recargar la página.
 */
export function suspendUnauthorizedReload(suspended) {
  unauthorizedReloadSuspended = Boolean(suspended);
}

function handleUnauthorized(response) {
  if (response.status === 401) {
    if (unauthorizedReloadSuspended) {
      throw new Error('La sesión no es válida mientras se reemplaza la base de datos (importación en curso).');
    }
    const hadToken = Boolean(localStorage.getItem('token'));
    localStorage.removeItem('token');
    if (hadToken && typeof window !== 'undefined' && !window.__sqmtAuthReloadScheduled) {
      window.__sqmtAuthReloadScheduled = true;
      window.setTimeout(() => {
        window.location.reload();
      }, 0);
    }
    throw new Error('Sesión expirada');
  }
}

/** fetch con Bearer token; devuelve Response (compatible con código legacy). */
export async function authFetch(url, options = {}) {
  const response = await executeAuthenticatedRequest(url, options);
  handleUnauthorized(response);
  return response;
}

export async function apiClient(url, options = {}) {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };
  if (isFormData && headers['Content-Type']) {
    delete headers['Content-Type'];
  }

  const startedAt = Date.now();
  const response = await executeAuthenticatedRequest(url, { ...options, headers });
  handleUnauthorized(response);

  const payload = await readResponsePayload(response, url, Date.now() - startedAt);

  if (!response.ok) {
    const detail = payload?.data?.detail;
    const err = new Error(
      formatErrorDetail(
        detail,
        `Error HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''} en ${requestPath(url)}.`
      )
    );
    err.status = response.status;
    err.detail = detail;
    err.payload = payload?.data;
    throw err;
  }

  return payload.data;
}

export { API_BASE_URL };
